import React, { useState, useEffect } from "react";
import { LinePath } from "@vx/shape";
import { Drag } from "@vx/drag";
import { curveBasis } from "@vx/curve";
import {
  Typography,
  Button,
  Grid,
  ButtonGroup,
  DialogTitle,
  Box,
  TextField,
  MenuItem,
  ButtonGroupProps,
  CircularProgress
} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import NotInterestedIcon from "@mui/icons-material/NotInterested";

import { tl } from "../../components/translate";
import {
  AssessmentTemplateImageCategories,
  AssessmentTemplateImageCategory
} from "../ResourceCentre/ModuleSettings/Settings/AssessmentTemplateImagesSetting";
import { AttachmentInterface } from "../../interfaces/AttachmentInterface";
import { AssessmentTemplateImageUpload } from "../ResourceCentre/ModuleSettings/Settings/AssessmentTemplateImageUpload";

interface RequireContext {
  keys: () => string[];
  (key: string): string;
}

interface WebpackRequire extends NodeJS.Require {
  context: (path: string, deep?: boolean, filter?: RegExp) => RequireContext;
}

const TEMPLATES_CONTEXT: RequireContext = (require as unknown as WebpackRequire).context(
  "../../../assets/images/assessmentTemplates",
  true
);

const getTemplatesByCategory = (category: string): string[] => [
  "",
  ...TEMPLATES_CONTEXT.keys()
    .filter((key) => key.startsWith(`./${category}/`))
    .map(TEMPLATES_CONTEXT)
];

const GENERAL_TEMPLATES = getTemplatesByCategory("general");
const EYE_TEMPLATES = getTemplatesByCategory("eye");
const DENTAL_TEMPLATES = getTemplatesByCategory("dental");
const DERMA_TEMPLATES = getTemplatesByCategory("derma");

interface DrawingBoardProps {
  closeModal: () => void;
  title: React.ReactNode;
  handleImageSave: (img: string, drawingBoardData: [], imageIndex: number) => void;
  width: number;
  height: number;
  boardData: any[];
  setBoardData: React.Dispatch<React.SetStateAction<any[]>>;
  imageIndex: number;
  setImageIndex: React.Dispatch<React.SetStateAction<number>>;
  isAssessmentForm: boolean;
  buttonGroupSize?: ButtonGroupProps["size"];
  rcTemplateImages?: AttachmentInterface[];
}

let svgPath: SVGSVGElement | null;
const thumbCache: Record<string, string> = {};

interface TemplateImage {
  url: string;
  isCustom: boolean; // custom images are already full URLs, no fetch needed
}

const DrawingBoard: React.FC<DrawingBoardProps> = ({
  closeModal,
  title,
  handleImageSave,
  width,
  height,
  boardData,
  setBoardData,
  imageIndex,
  setImageIndex,
  isAssessmentForm = false,
  buttonGroupSize = "small",
  rcTemplateImages = []
}) => {
  const [redoData, setRedoData] = useState([]);
  const [imageURL, setImageURL] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<string>(
    AssessmentTemplateImageCategory.GENERAL
  );
  const activeUrlRef = React.useRef("");

  // use memo
  const activeCategoryTemplates = React.useMemo((): TemplateImage[] => {
    // Merging static local templates with RC uploaded images for the active category
    if (!isAssessmentForm) return [];

    // static built-in images (index 0 is the "blank" sentinel "")
    let staticPaths: string[];

    switch (templateCategory) {
      case AssessmentTemplateImageCategory.DENTAL:
        staticPaths = DENTAL_TEMPLATES;
        break;
      case AssessmentTemplateImageCategory.EYE:
        staticPaths = EYE_TEMPLATES;
        break;
      case AssessmentTemplateImageCategory.DERMA:
        staticPaths = DERMA_TEMPLATES;
        break;
      default:
        staticPaths = GENERAL_TEMPLATES;
    }

    const staticImages: TemplateImage[] = staticPaths.map((url) => ({ url, isCustom: false }));

    // custom images uploaded by this RC for the selected category
    const customImages: TemplateImage[] = rcTemplateImages
      .filter((img) => img.entityType === templateCategory)
      .map((img) => ({ url: img.s3ResourceURL, isCustom: true }));

    // blank sentinel is index 0; append custom images after static ones
    return [...staticImages, ...customImages];
  }, [isAssessmentForm, templateCategory, rcTemplateImages]);

  // helper functions
  const getBase64FromUrl = async (url: string): Promise<void> => {
    setIsImageLoading(true);
    // ALL images - both static bundled and custom S3 URLs - must be fetched and converted
    // to an inline base64 data URI. When the SVG is serialised with XMLSerializer and later
    // rendered as a data:image/svg+xml;base64 string, browsers block external URLs inside
    // the <image href="https://..."> tag for security reasons, making the image blank/blurry.

    try {
      const response = await fetch(url, { cache: "reload" });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const base64data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const { result } = reader;
          if (typeof result === "string") resolve(result);
          else reject(new Error("Failed to convert image to base64"));
        };
        reader.onerror = () => reject(new Error("FileReader error"));
        reader.readAsDataURL(blob);
      });

      thumbCache[url] = base64data;
      if (activeUrlRef.current === url) {
        setImageURL(base64data);
      }
    } catch (error) {
      console.error("Error in getBase64FromUrl:", error);
      if (activeUrlRef.current === url) {
        setImageURL("");
      }
    } finally {
      if (activeUrlRef.current === url) {
        setIsImageLoading(false);
      }
    }
  };

  const downloadImageFn = () => {
    const xml = new XMLSerializer().serializeToString(svgPath);
    const svg64 = btoa(xml);
    const b64Start = "data:image/svg+xml;base64,";
    const image64 = b64Start + svg64;
    handleImageSave(image64, boardData, imageIndex);
    closeModal();
  };

  const clearFn = () => {
    setBoardData([]);
    setRedoData([]);
  };

  const undoFn = () => {
    if (boardData && boardData.length) {
      const updatedRedoData = redoData;
      updatedRedoData.push(boardData[boardData.length - 1]);
      setRedoData(updatedRedoData);
      const updatedBoardData = boardData.slice(0, -1);
      setBoardData(updatedBoardData);
    }
  };

  const redoFn = () => {
    if (redoData && redoData.length) {
      const lastElement = [redoData[redoData.length - 1]];
      const updatedBoardData = boardData.concat(lastElement);
      const updatedRedoData = redoData.slice(0, -1);
      setBoardData(updatedBoardData);
      setRedoData(updatedRedoData);
    }
  };

  // use effects
  useEffect(() => {
    const svg = document.getElementById("svg");
    svgPath = svg;
  });

  useEffect(() => {
    const activeTemplate = activeCategoryTemplates[imageIndex];
    const url = activeTemplate?.url || "";
    activeUrlRef.current = url;

    if (imageIndex !== 0 && url) {
      if (thumbCache[url]) {
        setImageURL(thumbCache[url]);
        setIsImageLoading(false);
      } else {
        getBase64FromUrl(url);
      }
    } else {
      setImageURL("");
      setIsImageLoading(false);
    }

    return () => {
      activeUrlRef.current = "";
    };
  }, [imageIndex, activeCategoryTemplates]);

  return (
    <>
      <DialogTitle sx={{ px: 0 }}>
        <Grid container>
          <Grid size={{ xs: 4 }}>
            {isAssessmentForm ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  value={templateCategory}
                  onChange={(e) => {
                    setTemplateCategory(e.target.value as string);
                    setImageIndex(0);
                    setImageURL("");
                  }}
                  sx={{
                    minWidth: 200
                  }}
                >
                  {AssessmentTemplateImageCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </TextField>

                <AssessmentTemplateImageUpload
                  category={templateCategory}
                  iconOnly
                  onUploadSuccess={() => {
                    // When a new image is uploaded, we want to select it automatically.
                    // The image will be added to rcTemplateImages (via Redux),
                    // which will cause activeCategoryTemplates to re-calculate.
                    // We can find the new index by matching the URL.
                    // Note: This matches based on s3ResourceURL.

                    // newIndex is usually the last one in our logic
                    const newIndex = activeCategoryTemplates.length;
                    setImageIndex(newIndex);
                    clearFn();
                  }}
                  disable={
                    !templateCategory ||
                    templateCategory === AssessmentTemplateImageCategory.GENERAL
                  }
                />
              </Box>
            ) : (
              <Typography>
                <b>{title}</b>
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 8 }} className="txtAlgnEnd">
            <ButtonGroup size={buttonGroupSize} aria-label="Small outlined button group">
              <Button
                onClick={() => {
                  undoFn();
                }}
                data-testmation="drawingBoardUndoButton"
              >
                {tl("assessment.undo")}
              </Button>
              <Button
                onClick={() => {
                  redoFn();
                }}
                data-testmation="drawingBoardRedoButton"
              >
                {tl("assessment.redo")}
              </Button>
              <Button
                onClick={() => {
                  clearFn();
                }}
                data-testmation="drawingBoardClearButton"
              >
                {tl("assessment.clear")}
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent style={{ padding: 0 }}>
        <Box
          display="flex"
          justifyContent="center"
          style={{
            border: "1px solid lightgrey",
            width: `calc(${width} + 1px)`,
            height: `calc(${height} + 1px)`
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            id="svg"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
          >
            <rect id="rect" fill="white" width={width} height={height} rx={5} />

            {!isImageLoading && (
              <image href={imageURL} width={width} height={height} style={{ opacity: 0.8 }} />
            )}

            {isImageLoading && (
              <foreignObject x="0" y="0" width={width} height={height}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  width="100%"
                >
                  <CircularProgress size={24} />
                </Box>
              </foreignObject>
            )}

            {boardData.map((d, i) => (
              <LinePath
                // eslint-disable-next-line react/no-array-index-key
                key={`line-${i}`}
                fill="transparent"
                stroke="black"
                strokeWidth={3}
                data={d}
                curve={curveBasis}
                x={(data: any) => data?.x}
                y={(data: any) => data?.y}
              />
            ))}

            <Drag
              width={width}
              height={height}
              resetOnStart
              onDragStart={({ x, y }) => {
                const newLine = [{ x, y }];
                const updatedBoardData = boardData.concat([newLine]);
                setBoardData(updatedBoardData);
              }}
              onDragMove={({ x, y, dx, dy }) => {
                const nextData = [...boardData];
                const point = [{ x: x + dx, y: y + dy }];
                const i = nextData.length - 1;
                nextData[i] = nextData[i].concat(point);
                setBoardData(nextData);
              }}
            >
              {({ x, y, dx, dy, isDragging, dragStart, dragEnd, dragMove }) => (
                <g>
                  {isDragging && (
                    <g>
                      <rect
                        fill="white"
                        width={8}
                        height={8}
                        x={x + dx - 4}
                        y={y + dy - 4}
                        className="pntEventNe"
                      />
                      <circle
                        cx={x}
                        cy={y}
                        r={4}
                        fill="transparent"
                        stroke="white"
                        className="pntEventNe"
                      />
                    </g>
                  )}
                  <rect
                    fill="transparent"
                    width={width}
                    height={height}
                    onMouseDown={dragStart}
                    onMouseUp={dragEnd}
                    onMouseMove={dragMove}
                    onTouchStart={dragStart}
                    onTouchEnd={dragEnd}
                    onTouchMove={dragMove}
                    style={{
                      cursor: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='%23666'%3E%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/%3E%3C/svg%3E") 3 21, crosshair`
                    }}
                  />
                </g>
              )}
            </Drag>
          </svg>
        </Box>
      </DialogContent>

      <DialogActions style={{ justifyContent: "space-between" }}>
        {/* image options */}
        <Box display="flex" alignItems="flex-start">
          {activeCategoryTemplates.map((template, index) => (
            <Box
              key={template.url || `blank-${index}`}
              onClick={() => {
                setImageIndex(index);
                clearFn();
              }}
              mr={0.8}
              sx={{
                border: index === imageIndex ? "2px solid" : "2px solid transparent"
              }}
            >
              {index === 0 && isAssessmentForm ? (
                <NotInterestedIcon
                  fontSize="large"
                  sx={{
                    display: "flex",
                    "&:hover": {
                      opacity: 0.6,
                      cursor: "pointer"
                    },
                    width: "30px",
                    height: "50px"
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  sx={{
                    "&:hover": {
                      opacity: 0.6,
                      cursor: "pointer"
                    }
                  }}
                >
                  <img
                    src={template.url}
                    alt={`template${index}`}
                    style={{
                      width: "30px",
                      height: "50px",
                      objectFit: "contain"
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* actions */}
        <Box alignItems="flex-end">
          <Button onClick={closeModal} data-testmation="drawinBoardCancelButton">
            {tl("assessment.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={downloadImageFn}
            data-testmation="drawingBoardSaveButton"
          >
            {tl("save")}
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default DrawingBoard;
